import React, { useCallback, useEffect, useState } from "react";
import { useAtom } from "jotai";
import { globalStore } from "../store/global.atoms";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Tab,
    TextField,
    Typography,
    Switch,
} from "@mui/material";
import {
    DeleteOutline,
    ExpandLess,
    ExpandMore,
    FilterAlt,
    FirstPage,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LastPage,
} from "@mui/icons-material";
import TabPanel from "../components/TabPanel";
import { DateTimePickerField } from "../components/DateTimePickerField";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;
const rowsPerPage = 8;

function a11yProps(index) {
    return {
        id: `billing-tab-${index}`,
        "aria-controls": `billing-tabpanel-${index}`,
    };
}

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
    const lastPage = Math.ceil(count / rowsPerPage) - 1;
    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={(e) => onPageChange(e, 0)}
                disabled={page === 0}
            >
                <FirstPage />
            </IconButton>
            <IconButton
                onClick={(e) => onPageChange(e, page - 1)}
                disabled={page === 0}
            >
                <KeyboardArrowLeft />
            </IconButton>
            <IconButton
                onClick={(e) => onPageChange(e, page + 1)}
                disabled={page >= lastPage}
            >
                <KeyboardArrowRight />
            </IconButton>
            <IconButton
                onClick={(e) => onPageChange(e, lastPage)}
                disabled={page >= lastPage}
            >
                <LastPage />
            </IconButton>
        </Box>
    );
}

function CreditGauge({ total, used, cycleStart, cycleEnd, planName }) {
    const cycleLabel =
        cycleStart && cycleEnd
            ? `Ciclo: ${cycleStart} – ${cycleEnd}`
            : cycleStart
              ? `A partir de ${cycleStart}`
              : null;

    if (total === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary" variant="body1">
                    Sem plano ativo
                </Typography>
                <Typography variant="h5" sx={{ mt: 1 }}>
                    {used} crédito{used !== 1 ? "s" : ""} utilizados
                </Typography>
                {cycleLabel && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                    >
                        {cycleLabel}
                    </Typography>
                )}
            </Box>
        );
    }

    const pct = (used / total) * 100;
    const gaugePct = Math.min(pct, 100);
    const gaugeColor =
        pct > 100
            ? "#b71c1c"
            : pct >= 80
              ? "#ff5722"
              : pct >= 50
                ? "#ff9800"
                : "#4caf50";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
            }}
        >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={140}
                    thickness={5}
                    sx={{ color: "rgba(0,0,0,0.08)", position: "absolute" }}
                />
                <CircularProgress
                    variant="determinate"
                    value={Math.max(gaugePct, 10)}
                    size={140}
                    thickness={5}
                    sx={{ color: gaugeColor }}
                />
                <Box
                    sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="h6" component="div">
                        {used}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        de {total}
                    </Typography>
                </Box>
            </Box>
            {planName && (
                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                    {planName}
                </Typography>
            )}
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: planName ? 0.5 : 1.5 }}
            >
                {pct.toFixed(1)}% dos créditos utilizados
            </Typography>
            {cycleLabel && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {cycleLabel}
                </Typography>
            )}
        </Box>
    );
}

function MessageDetail({ messages }) {
    return (
        <>
            {messages.map((msg, i) => {
                const isUser = msg.sender === "user";
                const topPt = i === 0 ? "10px" : "5px";
                return (
                    <React.Fragment key={i}>
                        <TableRow>
                            <TableCell
                                colSpan={4}
                                sx={{
                                    border: "none !important",
                                    padding: `${topPt} 8px 5px !important`,
                                    backgroundColor: "white !important",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        backgroundColor: isUser
                                            ? "#d6dee7"
                                            : "#f5f5f5",
                                        borderRadius: "8px",
                                        px: 2,
                                        py: 1.5,
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
                                        "&:hover": {
                                            outline: "1px solid #ccc",
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: isUser ? "#555" : "#0359D6",
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                            minWidth: 84,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isUser ? "Usuário" : "Assistente"}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            color: "#333",
                                            flex: 1,
                                        }}
                                    >
                                        {msg.text}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            minWidth: 130,
                                            textAlign: "right",
                                        }}
                                    >
                                        {msg.createdOn}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: isUser
                                                ? "text.disabled"
                                                : "inherit",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            minWidth: 48,
                                            textAlign: "center",
                                        }}
                                    >
                                        {isUser ? "—" : msg.creditsUsed}
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                        {msg.splitParts &&
                            msg.splitParts.map((part, j) => (
                                <TableRow
                                    key={`sp-${j}`}
                                    sx={{
                                        "& > td": {
                                            borderBottom: "none !important",
                                        },
                                    }}
                                >
                                    <TableCell
                                        colSpan={4}
                                        sx={{
                                            border: "none !important",
                                            padding: `5px 8px 5px !important`,
                                            backgroundColor: "white !important",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: "8px",
                                                px: 2,
                                                py: 1.5,
                                                boxShadow:
                                                    "0 1px 4px rgba(0,0,0,0.09)",
                                                "&:hover": {
                                                    outline: "1px solid #ccc",
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#0359D6",
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                    minWidth: 84,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                ↳ Assistente
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "pre-wrap",
                                                    color: "#333",
                                                    flex: 1,
                                                }}
                                            >
                                                {part.text}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    flexShrink: 0,
                                                    minWidth: 130,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {part.createdOn}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    flexShrink: 0,
                                                    minWidth: 48,
                                                    textAlign: "center",
                                                }}
                                            >
                                                0
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </React.Fragment>
                );
            })}
        </>
    );
}

const toDatetimeStr = (filter, endOfDay = false) => {
    if (!filter?.date) return "";
    const [day, month, year] = filter.date.split("/");
    const formattedDate = `${year}-${month}-${day}`;
    if (filter.time)
        return `${formattedDate} ${filter.time}:${endOfDay ? "59" : "00"}`;
    return `${formattedDate} ${endOfDay ? "23:59:59" : "00:00:00"}`;
};

function BillingPage() {
    const [activeTab, setActiveTab] = useState(0);
    const [billingData] = useAtom(globalStore.billingDataAtom);
    const credit = billingData?.credit ?? { total: 0, used: 0 };
    const currentPlan = billingData?.subscription?.currentPlan ?? {
        planName: null,
        cycleStart: null,
        cycleEnd: null,
    };
    const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(false);

    const [transactions, setTransactions] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [expandedRow, setExpandedRow] = useState(null);

    // Cycle dashboard state
    const currentYear = new Date().getFullYear();
    const todayStr = new Date().toISOString().slice(0, 10);
    const [cyclesData, setCyclesData] = useState([]);
    const [startDateCycleFilter, setStartDateCycleFilter] = useState(
        `${currentYear}-01-01`,
    );
    const [endDateCycleFilter, setEndDateCycleFilter] = useState(todayStr);
    const [appliedStartCycle, setAppliedStartCycle] = useState(
        `${currentYear}-01-01`,
    );
    const [appliedEndCycle, setAppliedEndCycle] = useState(todayStr);

    // Draft filter state
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [sessionIdFilter, setSessionIdFilter] = useState("");

    // Applied filter state (triggers fetch)
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");
    const [appliedSessionId, setAppliedSessionId] = useState("");

    const isFilterApplied =
        appliedStartDate !== "" ||
        appliedEndDate !== "" ||
        appliedSessionId !== "";

    const offset = page * rowsPerPage;

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const loadTransactions = useCallback(async () => {
        const params = new URLSearchParams({ limit: rowsPerPage, offset });
        if (appliedStartDate) params.append("start_datetime", appliedStartDate);
        if (appliedEndDate) params.append("end_datetime", appliedEndDate);
        if (appliedSessionId) params.append("session_id", appliedSessionId);

        const res = await fetch(
            `${backendEndpoint}/billing/credits/detailed/?${params.toString()}`,
            {
                credentials: "include",
                headers: { "X-CSRF-Token": getCookieValue("csrfToken") },
            },
        );
        if (res.ok) {
            const json = await res.json();
            setTransactions(json.transactions ?? []);
            setTotal(json.total ?? 0);
        }
    }, [
        offset,
        getCookieValue,
        appliedStartDate,
        appliedEndDate,
        appliedSessionId,
    ]);

    const loadCycles = useCallback(async () => {
        const params = new URLSearchParams();
        if (appliedStartCycle) params.append("start_date", appliedStartCycle);
        if (appliedEndCycle) params.append("end_date", appliedEndCycle);
        const res = await fetch(
            `${backendEndpoint}/billing/credits/cycles/?${params.toString()}`,
            {
                credentials: "include",
                headers: { "X-CSRF-Token": getCookieValue("csrfToken") },
            },
        );
        if (res.ok) {
            const json = await res.json();
            setCyclesData(json.cycles ?? []);
        }
    }, [getCookieValue, appliedStartCycle, appliedEndCycle]);

    const handleToggleOverage = useCallback(async () => {
        const newValue = !autoRechargeEnabled;
        setAutoRechargeEnabled(newValue);

        await fetch(`${backendEndpoint}/billing/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
            body: JSON.stringify({ autoRecharge: { enabled: newValue } }),
        });
    }, [autoRechargeEnabled, getCookieValue]);

    const handleSearch = () => {
        setAppliedStartDate(toDatetimeStr(startDateFilter));
        setAppliedEndDate(toDatetimeStr(endDateFilter, true));
        setAppliedSessionId(sessionIdFilter.trim());
        setPage(0);
    };

    const handleClearFilters = () => {
        setStartDateFilter("");
        setEndDateFilter("");
        setSessionIdFilter("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setAppliedSessionId("");
        setPage(0);
    };

    const handleChangePage = (e, newPage) => setPage(newPage);

    const handleToggleRow = (key) =>
        setExpandedRow(expandedRow === key ? null : key);

    useEffect(() => {
        if (activeTab === 1) {
            loadCycles();
        } else if (activeTab === 2) {
            loadTransactions();
        }
    }, [activeTab, loadCycles, loadTransactions]);

    useEffect(() => {
        setAutoRechargeEnabled(!!billingData?.autoRecharge?.enabled);
    }, [billingData?.autoRecharge?.enabled]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Consumo
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Visão Geral" {...a11yProps(0)} />
                    <Tab label="Consumo por Ciclo" {...a11yProps(1)} />
                    <Tab label="Histórico de Consumo" {...a11yProps(2)} />
                </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
                <CreditGauge
                    total={credit.total}
                    used={credit.used}
                    cycleStart={currentPlan.cycleStart}
                    cycleEnd={currentPlan.cycleEnd}
                    planName={currentPlan.planName}
                />
                <Box
                    sx={{
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: "#f5f5f5",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Switch
                            checked={autoRechargeEnabled}
                            onChange={handleToggleOverage}
                        />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                Permitir uso de créditos adicionais além do
                                limite do plano
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Quando ativado, o uso continuará e o excedente
                                será cobrado ao final do ciclo. Quando
                                desativado, o uso será bloqueado ao atingir o
                                limite do plano.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <Box
                    sx={{
                        maxWidth: 900,
                        mx: "auto",
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "nowrap",
                        alignItems: "flex-end",
                        mb: 2,
                    }}
                >
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                            Data início
                        </Box>
                        <DateTimePickerField
                            value={startDateFilter}
                            onChange={setStartDateFilter}
                            placeholder="Data início..."
                            defaultTime="00:00"
                        />
                    </Box>
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                            Data fim
                        </Box>
                        <DateTimePickerField
                            value={endDateFilter}
                            onChange={setEndDateFilter}
                            placeholder="Data fim..."
                            defaultTime="23:59"
                        />
                    </Box>
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                            Conversa
                        </Box>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="ID da conversa..."
                            value={sessionIdFilter}
                            onChange={(e) => setSessionIdFilter(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleSearch()
                            }
                            sx={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<FilterAlt />}
                        onClick={handleSearch}
                        sx={{
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mb: 0.2,
                        }}
                    >
                        Filtrar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DeleteOutline />}
                        onClick={handleClearFilters}
                        disabled={!isFilterApplied}
                        sx={{
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mb: 0.2,
                        }}
                    >
                        Limpar
                    </Button>
                </Box>

                <TableContainer sx={{ maxWidth: 900, mx: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#E2E8F0" }}>
                                <TableCell sx={{ width: 40 }} />
                                <TableCell>Conversa</TableCell>
                                <TableCell>Data</TableCell>
                                <TableCell align="center">Créditos</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map((tx, i) => {
                                const rowKey =
                                    tx.sessionId ?? `sessionless-${i}`;
                                const isExpanded = expandedRow === rowKey;
                                const hasMessages =
                                    tx.messages && tx.messages.length > 0;

                                return (
                                    <React.Fragment key={rowKey}>
                                        <TableRow
                                            hover
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor:
                                                        "white !important",
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ p: 0.5 }}>
                                                {hasMessages && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleToggleRow(
                                                                rowKey,
                                                            )
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLess fontSize="small" />
                                                        ) : (
                                                            <ExpandMore fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        fontSize: 12,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    {tx.sessionId ?? "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {tx.createdOn ?? "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2">
                                                    {tx.creditsUsed}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {hasMessages && isExpanded && (
                                            <MessageDetail
                                                messages={tx.messages}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {transactions.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        align="center"
                                        sx={{ py: 4 }}
                                    >
                                        <Typography
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            Nenhuma interação registrada ainda.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        {total !== null && total > 0 && (
                            <TableFooter sx={{ backgroundColor: "#E2E8F0" }}>
                                <TableRow>
                                    <TablePagination
                                        colSpan={4}
                                        rowsPerPageOptions={[rowsPerPage]}
                                        count={total}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                {/* Cycle filters */}
                <Box
                    sx={{
                        mx: "auto",
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-end",
                        mb: 3,
                        flexWrap: "nowrap",
                    }}
                >
                    <Box>
                        <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                            Data início
                        </Box>
                        <TextField
                            type="date"
                            size="small"
                            value={startDateCycleFilter}
                            onChange={(e) =>
                                setStartDateCycleFilter(e.target.value)
                            }
                            sx={{ backgroundColor: "white" }}
                        />
                    </Box>
                    <Box>
                        <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                            Data fim
                        </Box>
                        <TextField
                            type="date"
                            size="small"
                            value={endDateCycleFilter}
                            onChange={(e) =>
                                setEndDateCycleFilter(e.target.value)
                            }
                            sx={{ backgroundColor: "white" }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<FilterAlt />}
                        onClick={() => {
                            setAppliedStartCycle(startDateCycleFilter);
                            setAppliedEndCycle(endDateCycleFilter);
                        }}
                        sx={{
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mb: 0.2,
                        }}
                    >
                        Filtrar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DeleteOutline />}
                        disabled={
                            appliedStartCycle === `${currentYear}-01-01` &&
                            appliedEndCycle === todayStr
                        }
                        onClick={() => {
                            setStartDateCycleFilter(`${currentYear}-01-01`);
                            setEndDateCycleFilter(todayStr);
                            setAppliedStartCycle(`${currentYear}-01-01`);
                            setAppliedEndCycle(todayStr);
                        }}
                        sx={{
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mb: 0.2,
                        }}
                    >
                        Limpar
                    </Button>
                </Box>

                {/* Bar chart */}
                <Box sx={{ mx: "auto" }}>
                    {cyclesData.length === 0 ? (
                        <Typography
                            color="text.secondary"
                            align="center"
                            sx={{ py: 4 }}
                        >
                            Nenhuma interação registrada no período.
                        </Typography>
                    ) : (
                        <ResponsiveContainer width="100%" height={450}>
                            <BarChart
                                data={cyclesData}
                                margin={{
                                    top: 8,
                                    right: 16,
                                    left: 0,
                                    bottom: 20,
                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="cycleStart"
                                    tick={{
                                        fontSize: 10,
                                        angle: -45,
                                        textAnchor: "end",
                                    }}
                                    height={80}
                                    tickFormatter={(v, i) => {
                                        const end =
                                            cyclesData[i]?.cycleEnd ?? "";
                                        return `${v} – ${end}`;
                                    }}
                                />
                                <YAxis
                                    tick={{ fontSize: 12 }}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    formatter={(value) => [
                                        `${value} crédito${value !== 1 ? "s" : ""}`,
                                        "Uso",
                                    ]}
                                    labelFormatter={(label, payload) => {
                                        if (!payload?.length) return label;
                                        const { cycleStart, cycleEnd } =
                                            payload[0].payload;
                                        return `${cycleStart} – ${cycleEnd}`;
                                    }}
                                />
                                {credit.total > 0 && (
                                    <ReferenceLine
                                        y={credit.total}
                                        stroke="#666"
                                        strokeDasharray="4 4"
                                        label={{
                                            value: `Plano: ${credit.total}`,
                                            position: "insideTopRight",
                                            fontSize: 11,
                                            fill: "#666",
                                        }}
                                    />
                                )}
                                <Bar
                                    dataKey="creditsUsed"
                                    radius={[4, 4, 0, 0]}
                                >
                                    {cyclesData.map((entry, index) => {
                                        const p =
                                            credit.total > 0
                                                ? (entry.creditsUsed /
                                                      credit.total) *
                                                  100
                                                : 0;
                                        const fill =
                                            p > 100
                                                ? "#b71c1c"
                                                : p >= 80
                                                  ? "#ff5722"
                                                  : p >= 50
                                                    ? "#ff9800"
                                                    : "#4caf50";
                                        return <Cell key={index} fill={fill} />;
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            </TabPanel>
        </Box>
    );
}

export default BillingPage;
